import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { submitVideoToIndexNow } from "@/utils/indexnow";

export interface Video {
  id: string;
  titulo: string;
  descripcion: string | null;
  video_url: string;
  youtube_id: string;
  thumbnail_url: string | null;
  slug: string;
  categoria: string;
  etiquetas: string[];
  destacado: boolean;
  activo: boolean;
  vistas: number;
  votos_positivos: number;
  votos_negativos: number;
  puntuacion: number;
  orden_visualizacion: number;
  creado_en: string;
  publicado_en: string | null;
  creado_por: string | null;
  status: 'pending' | 'approved' | 'rejected';
  info_url: string | null;
}

export const useVideos = (filters?: { categoria?: string; destacado?: boolean; limit?: number; showAll?: boolean }) => {
  return useQuery({
    queryKey: ["videos", filters],
    queryFn: async () => {
      // Fetch more videos than needed to enable randomization
      const fetchLimit = filters?.limit ? Math.min(filters.limit * 5, 50) : 50;
      
      let query = supabase
        .from("videos")
        .select("*");

      // If not showing all (admin view), only show approved and active videos
      if (!filters?.showAll) {
        query = query.eq("status", "approved").eq("activo", true);
      }

      if (filters?.categoria && filters.categoria !== "all") {
        query = query.eq("categoria", filters.categoria);
      }

      if (filters?.destacado) {
        query = query.eq("destacado", true);
      }

      query = query.limit(fetchLimit);

      const { data, error } = await query;

      if (error) throw error;
      
      // Randomize results
      const shuffled = (data || []).sort(() => Math.random() - 0.5);
      
      // Apply limit after shuffling
      const finalData = filters?.limit ? shuffled.slice(0, filters.limit) : shuffled;
      
      return finalData as Video[];
    },
  });
};

export const useMyVideos = () => {
  return useQuery({
    queryKey: ["my-videos"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user logged in");

      const { data, error } = await supabase
        .from("videos")
        .select("*")
        .eq("creado_por", user.id)
        .order("creado_en", { ascending: false });

      if (error) throw error;
      return data as Video[];
    },
  });
};

export const useVideo = (slug: string) => {
  return useQuery({
    queryKey: ["video", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("videos")
        .select("*")
        .eq("slug", slug)
        .eq("activo", true)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Video not found");

      // Increment view count
      await supabase.rpc("increment_video_views", { video_id: data.id });

      return data as Video;
    },
  });
};

export const useCreateVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (video: Partial<Video>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      const videoData = {
        ...video,
        creado_por: user.id,
        status: 'pending' as const,
      };

      const { data, error } = await supabase
        .from("videos")
        .insert([videoData as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      queryClient.invalidateQueries({ queryKey: ["my-videos"] });
      toast.success("Video enviado para revisión");
    },
    onError: (error) => {
      toast.error(`Error al crear video: ${error.message}`);
    },
  });
};

export const useApproveVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      const { data, error } = await supabase
        .from("videos")
        .update({ 
          status: approved ? 'approved' : 'rejected',
          activo: approved 
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      
      // Submit to IndexNow when approving
      if (variables.approved && data && data.slug) {
        submitVideoToIndexNow(data.slug);
      }
      
      toast.success(variables.approved ? "Video aprobado" : "Video rechazado");
    },
    onError: (error) => {
      toast.error(`Error: ${error.message}`);
    },
  });
};

export const useUpdateVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Video> & { id: string }) => {
      const { data, error } = await supabase
        .from("videos")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video actualizado exitosamente");
    },
    onError: (error) => {
      toast.error(`Error al actualizar video: ${error.message}`);
    },
  });
};

export const useDeleteVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("videos").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video eliminado exitosamente");
    },
    onError: (error) => {
      toast.error(`Error al eliminar video: ${error.message}`);
    },
  });
};

export const useExtractVideoInfo = () => {
  return useMutation({
    mutationFn: async (videoUrl: string) => {
      const { data, error } = await supabase.functions.invoke("extract-video-info", {
        body: { videoUrl },
      });

      // Check if we have a custom error message in the data first
      // (this happens when edge function returns non-2xx status)
      if (data && !data.success && data.error) {
        throw new Error(data.error);
      }

      // Then check for generic network errors
      if (error) {
        throw new Error(error.message || 'Error al llamar a la función');
      }

      return data.videoInfo;
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useOptimizeContent = () => {
  return useMutation({
    mutationFn: async ({ title, description, tags }: { title: string; description?: string; tags?: string[] }) => {
      const { data, error } = await supabase.functions.invoke("rewrite-video-content", {
        body: { title, description, tags },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      return data.optimizedContent;
    },
    onSuccess: () => {
      toast.success("Contenido optimizado con IA");
    },
    onError: (error) => {
      toast.error(`Error al optimizar: ${error.message}`);
    },
  });
};
